import React, { PureComponent } from "react";

import AudioWidget from "@fdmg/ts-react-audio-widget";
import styled from "styled-components";
import PlayerHeadings from "./PlayerHeadings";
import PlayPauseButtonComponent from "../components/PlayPauseButtonComponent";
import PlayerImageComponent from "../components/PlayerImageComponent";
import IconComponent from "../components/IconComponent";

import { ICONS } from "../data/constants";
import { convertToReadableTime } from "../utils/ConvertTime";

export interface CustomWindow extends Window {
  analyticsEvent: (
    eventCategory: string,
    eventAction: string,
    eventLabel: number | string,
    eventValue: number | string | null,
    eventNonInt: boolean | string | null
  ) => void;
}

declare var window: CustomWindow;

export interface Props {
  isCustomPlaylist?: boolean;
  podcastTitle: any;
  websiteUrl: any;
  artworkUrl: any;
  sponsors: any;
  episodes: any;
  programIndex: any;
  programType: any;
  customColors?: any;
  hidePodcastTitle?: any;
  hideEpisodeTitle?: any;
  episodeTitle?: any;
  playlistIndex?: boolean;
  handleProgramIndexChange?: (...args: any[]) => void;
}
/**
 * AudioWidget player composition - Renders all the audioplayer's components.
 * ?customColors=#4393ab,black,#ffead9,black,#fff
 */
export default class Player extends PureComponent<Props, any> {
  /**
   * We need to access a lot of properties from the AudioWidget in order
   * to control the audio element.
   */
  private audioComponent: AudioWidget;
  private playerPoll: any;

  constructor(props: Props) {
    super(props);
    this.state = {
      buffering: false,
      isPlaying: false,
      playerEndEnabled: false,
      programIndex: this.props.programIndex,
      passedPercentage: 0,
      playerType: props.programType ? "podcast" : "nieuwsfragment",
      hasCustomColors: this.props.customColors.length > 0 ? true : false,
      elapsedIntervalAmount: 0,
      maxInterVal: 30,
      totalTimeSpent: 0
    };

    window.onbeforeunload = this.analyticsBeforeUnload;
  }

  calculateTimeLeft = () => {
    const currentTime = Math.floor(
      this.audioComponent.audioElement.currentTime
    );
    const duration = Math.floor(this.audioComponent.audioElement.duration);
    const timeLeft = (duration - currentTime) * 1000;
    this.setState({ timeLeft: convertToReadableTime(timeLeft, "u:mm:ss") });
  };

  componentDidUpdate(prevProps, prevState) {
    if (this.state.playTimeStamp !== prevState.playTimeStamp) {
      this.audioComponent.play();
    }

    if (
      this.props.episodes[this.state.programIndex].title !==
      prevProps.episodes[prevState.programIndex].title
    ) {
      this.resetTimerValues();
    }

    if (this.state.programIndex !== this.props.programIndex) {
      if (this.state.playerEndEnabled) {
        this.analyticsPlayerEnd();
      }
      this.setState({ programIndex: this.props.programIndex });
      this.handleOnPlay();
    }
  }

  /**
   * Also fires when the player ended and skips to the next song automatically.
   * Analytics on Play event for the audio player: Fires when the user presses play by clicking the play or next/previous buttons.
   */
  analyticsOnPlayAudio = () => {
    window.analyticsEvent(
      this.state.playerType,
      "play",
      `${this.props.podcastTitle}_${
        this.props.episodes[this.state.programIndex].title
      }`,
      null,
      null
    );
  };

  /**
   * Analytics on Play event for the audio player.
   */
  analyticsOnPauseAudio = () => {
    window.analyticsEvent(
      this.state.playerType,
      "pause",
      `${this.props.podcastTitle}_${
        this.props.episodes[this.state.programIndex].title
      }`,
      null,
      null
    );
  };

  /**
   * Analytics on Percentage event for the audio player: Sends an event on every +10%.
   */
  analyticsPercentage = () => {
    const percentages = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
    const currentPercentage = Math.floor(
      (this.audioComponent.audioElement.currentTime /
        this.audioComponent.audioElement.duration) *
        100
    );
    for (const percentage of percentages) {
      if (
        currentPercentage === percentage &&
        currentPercentage !== this.state.passedPercentage
      ) {
        this.setState({ passedPercentage: currentPercentage });
        window.analyticsEvent(
          this.state.playerType,
          "percentage",
          `${currentPercentage}_${this.props.podcastTitle}_${
            this.props.episodes[this.state.programIndex].title
          }`,
          null,
          null
        );
      }
    }
  };

  /**
   * Analytics on unload event for the audio player: When the user refreshes or closes the page (note: this does not work on all browsers/devices).
   * Will only send an event if the user actually used the audio player.
   */
  analyticsBeforeUnload = () => {
    const playerElapsed = Math.floor(
      this.audioComponent.audioElement.currentTime
    );
    if (this.state.playerEndEnabled === true) {
      window.analyticsEvent(
        this.state.playerType,
        "close",
        `${playerElapsed}_${this.props.podcastTitle}_${
          this.props.episodes[this.state.programIndex].title
        }`,
        null,
        null
      );
    }
  };

  /**
   * Analytics on End event for the audio player: when the user skips to previous/next audio file or when the audio
   * automatically skips to the next audio file when the previous audio file completed.
   */
  analyticsPlayerEnd = () => {
    const playerElapsed = Math.floor(
      this.audioComponent.audioElement.currentTime
    );
    window.analyticsEvent(
      this.state.playerType,
      "end",
      `${playerElapsed}_${this.props.podcastTitle}_${
        this.props.episodes[this.state.programIndex].title
      }`,
      null,
      null
    );
  };

  analyticsPollEvent = () => {
    window.analyticsEvent(
      this.state.playerType,
      "listeningtime",
      `${this.state.totalTimeSpent}_${this.props.podcastTitle}_${
        this.props.episodes[this.state.programIndex].title
      }`,
      null,
      null
    );
  };

  resetTimerValues() {
    this.setState({
      elapsedIntervalAmount: 0,
      totalTimeSpent: 0
    });
  }

  /**
   * Loads the next audio.
   */
  loadNextAudio = () => {
    this.resetTimerValues();
    this.setProgramIndex(this.state.programIndex + 1);
  };

  /**
   * Loads the previous audio.
   */
  loadPreviousAudio = () => {
    const currentTime = this.audioComponent.audioElement.currentTime;
    if (currentTime > 2) {
      this.audioComponent.audioElement.currentTime = 0;
    } else {
      this.resetTimerValues();
      this.setProgramIndex(this.state.programIndex - 1);
    }
  };

  onTimeUpdate = () => {
    this.analyticsPercentage();
    this.calculateTimeLeft();
  };

  /**
   * Toggles play/pause when the playPause button is clicked. No other
   * user interaction should call this function.
   */
  onPlayPause = () => {
    if (this.state.isPlaying) {
      this.handleOnPause();
    } else {
      this.handleOnPlay();
    }
  };

  startPlayerPoll() {
    clearInterval(this.playerPoll);
    this.playerPoll = setInterval(() => {
      if (this.state.isPlaying) {
        this.setState({ totalTimeSpent: this.state.totalTimeSpent + 1 });
        this.setState({
          elapsedIntervalAmount: this.state.elapsedIntervalAmount + 1
        });
        if (this.state.elapsedIntervalAmount >= this.state.maxInterVal) {
          this.setState({ elapsedIntervalAmount: 0 });
          this.analyticsPollEvent();
        }
      }
    }, 1000);
  }

  /**
   * Pause the audio player.
   */
  handleOnPause = (...args) => {
    this.analyticsOnPauseAudio();
    this.setState({ isPlaying: false });
    this.audioComponent.pause();
  };

  onAudioComponentPause = () => {
    this.setState({ isPlaying: false });
  };

  /**
   * Is called when the playPause button is clicked and the player
   * is not in a playing state. Also gets called whenever the program
   * index changes.
   */
  handleOnPlay = (...args) => {
    document.documentElement.classList.add("playback-enabled");
    this.analyticsOnPlayAudio();
    this.startPlayerPoll();
    this.setState({
      isPlaying: true,
      playerEndEnabled: true,
      playTimeStamp: new Date()
    });
    this.audioComponent.play();
  };

  onAudioComponentPlay = () => {
    this.setState({
      isPlaying: true,
      playerEndEnabled: true
    });
  };

  /**
   * Makes sure index never goes out-of-bounds by looping the index.
   */
  setProgramIndex = index => {
    if (index < 0) {
      this.props.handleProgramIndexChange(this.state.programIndex);
    } else if (index >= this.props.episodes.length) {
      this.props.handleProgramIndexChange(0);
    } else {
      this.props.handleProgramIndexChange(index);
    }
  };

  /**
   * Render all the components.
   * TODO: Figure out why we can't use the Anchor component for the buttons as google tag manager
   * automatically loads an extra script tag on every <A target="_self" ....> click.
   */
  render() {
    return (
      <PlayerWrapper
        customColors={this.state.hasCustomColors ? this.props.customColors : []}
      >
        <AudioPlayer className="player">
          <PlayerImageComponent
            className="player-image"
            alt="Episode image"
            src={this.props.artworkUrl}
          />
          <PlayPauseButtonComponent
            isPlaying={this.state.isPlaying}
            onPlayPause={this.onPlayPause}
            customColors={
              this.state.hasCustomColors ? this.props.customColors : []
            }
          />
          <MetaData className={`metadata ${this.state.playerType}`}>
            <HeadingWrapper>
              <PlayerHeadings
                websiteUrl={this.props.websiteUrl}
                podcastTitle={this.props.podcastTitle}
                episodes={this.props.episodes}
                customColors={this.props.customColors}
                hidePodcastTitle={this.props.hidePodcastTitle}
                hideEpisodeTitle={this.props.hideEpisodeTitle}
                episodeTitle={this.props.episodeTitle}
                programIndex={this.props.programIndex}
                isCustomPlaylist={this.props.isCustomPlaylist}
                playlistIndex={this.props.playlistIndex}
              />
            </HeadingWrapper>

            <Indicators
              className={"indicators"}
              customColors={
                this.state.hasCustomColors ? this.props.customColors : []
              }
            >
              <PlayerControls className="player-controls previous-button">
                <IconComponent
                  icon={ICONS.PREVIOUS}
                  color={
                    this.state.hasCustomColors
                      ? this.props.customColors[3]
                      : "#ffcb2e"
                  }
                  size={22}
                  onClick={this.loadPreviousAudio}
                />
              </PlayerControls>
              <AudioWidget
                playerSrc={
                  this.props.episodes[this.state.programIndex].media[1].url
                }
                onEnded={this.loadNextAudio}
                onTimeUpdate={this.onTimeUpdate}
                ref={ref => {
                  this.audioComponent = ref!;
                }}
                hideProgressBarCurrentTime={true}
                hideProgressBarDuration={true}
                onPlay={this.onAudioComponentPlay}
                onPause={this.onAudioComponentPause}
                preload={"none"}
              />

              {this.props.episodes.length <= 1 ? (
                ""
              ) : (
                <PlayerControls className="player-controls next-button">
                  <IconComponent
                    icon={ICONS.NEXT}
                    color={
                      this.state.hasCustomColors
                        ? this.props.customColors[3]
                        : "#ffcb2e"
                    }
                    size={22}
                    onClick={this.loadNextAudio}
                  />
                </PlayerControls>
              )}
              {this.state.timeLeft ? (
                <div className="time-left-counter">
                  <span>
                    {this.state.timeLeft !== "NaN"
                      ? `-  ${this.state.timeLeft}`
                      : ""}
                  </span>
                </div>
              ) : (
                ""
              )}
            </Indicators>
          </MetaData>
        </AudioPlayer>
      </PlayerWrapper>
    );
  }
}

const HeadingWrapper: any = styled.div`
  min-height: 34px;
`;

const Indicators: any = styled.div`
  display: -ms-flexbox;
  display: flex;
  -ms-flex-direction: row;
  flex-direction: row;
  margin-top: -4px;
  .time-left-counter {
    color: ${(props: any) =>
      props.customColors.length > 0 ? props.customColors[1] : "#fff"};
    font-size: 12px;
    justify-content: flex-end;
    align-items: center;
    display: -ms-flexbox;
    display: flex;
    opacity: 0.7;
    min-width: 55px;
    margin-top: -5px;
    padding-right: 2px;
    span {
      margin-left: 5px;
    }
  }
  .progress {
    height: 30px;
    padding: 0 5px;
    .counters {
      display: flex;
      justify-content: space-between;
      padding-top: 10px;
      > span {
        font-size: 10px;
        margin-top: 10px;
        color: rgba(152, 152, 152, 1);
      }
      height: 11px;
      &.hidden {
        display: none;
      }
      &.visible {
        display: inherit;
      }
    }

    .time-line {
      position: relative;
      &:before {
        content: "";
        position: absolute;
        top: 12px;
        border-radius: 5px;
        width: 100%;
        height: 3px;
        display: block;
        box-shadow: inset 0 0 15px rgba(0, 0, 0, 0.15);
      }
      .time-line-progress-bar {
        position: absolute;
        top: 12px;
        height: 3px;
        display: block;
        width: 0;
        background-color: ${(props: any) =>
          props.customColors.length > 0 ? props.customColors[0] : "#ffd200"};
        border-top-left-radius: 5px;
        border-bottom-left-radius: 5px;
        &:after {
          content: "";
          position: absolute;
          right: -4px;
          height: 9px;
          width: 9px;
          border-radius: 50%;
          background-color: ${(props: any) =>
            props.customColors.length > 0 ? props.customColors[0] : "#ffd200"};
          border: transparent;
          margin-top: 0;
          top: -3px;
        }
      }
      input[type="range"] {
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        width: 100%;
        position: absolute;
        background: transparent;
        margin: 0;
        padding: 0;
        opacity: 0;
        height: 30px;
        &:hover {
          cursor: pointer;
        }
        &:focus {
          outline: none;
        }
        &::-webkit-slider-thumb {
          position: relative;
          -webkit-appearance: none;
          border-radius: 50%;
          background-color: ${(props: any) =>
            props.customColors.length > 0 ? props.customColors[0] : "#ffd200"};
          border: transparent;
          cursor: pointer;
          margin-top: 2px;
          top: 0;
          height: 20px;
          width: 20px;
          opacity: 0;
        }
        &::-moz-range-thumb {
          -moz-appearance: none;
          position: absolute;
          border-radius: 50%;
          background-color: ${(props: any) =>
            props.customColors.length > 0 ? props.customColors[0] : "#ffd200"};
          border: transparent;
          cursor: pointer;
          height: 20px;
          width: 20px;
          opacity: 0;
        }
        &::-moz-range-track {
          -moz-appearance: none;
          display: none;
          background-color: transparent;
          cursor: pointer;
        }
        &::-ms-thumb,
        &::-ms-track,
        &::-ms-fill-upper,
        &::-ms-fill-lower {
          opacity: 0;
        }
      }
    }

    .loader {
      width: 17px;
      height: 17px;
      position: absolute;
      right: -8px;
      top: -7px;
    }

    .double-bounce1,
    .double-bounce2 {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background-color: ${(props: any) =>
        props.customColors.length > 0 ? props.customColors[0] : "#ffd200"};
      opacity: 0.6;
      position: absolute;
      top: 0;
      left: 0;

      -webkit-animation: sk-bounce 2s infinite ease-in-out;
      animation: sk-bounce 2s infinite ease-in-out;
    }

    .double-bounce2 {
      -webkit-animation-delay: -1s;
      animation-delay: -1s;
    }

    @-webkit-keyframes sk-bounce {
      0%,
      100% {
        -webkit-transform: scale(0);
      }
      50% {
        -webkit-transform: scale(1);
      }
    }

    @keyframes sk-bounce {
      0%,
      100% {
        transform: scale(0);
        -webkit-transform: scale(0);
      }
      50% {
        transform: scale(1);
        -webkit-transform: scale(1);
      }
    }
  }
`;

const MetaData: any = styled.div`
  margin-left: 5px;
  overflow: hidden;
  max-width: 100%;
  width: 100%;
  display: -ms-flexbox;
  display: flex;
  -ms-flex-direction: column;
  flex-direction: column;
  padding-top: 5px;
  padding-right: 5px;
  a {
    display: block;
    &.show-title {
      h2 {
        font-weight: normal;
      }
    }
    &.show-title,
    &.episode-title {
      margin-bottom: 3px;
      margin-right: 25px;
    }
  }
`;

const AudioPlayer: any = styled.div`
  display: -ms-flexbox;
  display: flex;
  position: relative;
  .progress {
    padding: 0;
    margin-top: 5px;
  }
  h1,
  h2 {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const PlayerControls: any = styled.div`
  max-width: 100%;
  margin: 0;
  width: 25px;
  a {
    color: #ffcb2e;
  }
  &.previous-button a {
    margin-right: 5px;
  }
  &.next-button a {
    margin-left: 5px;
  }
  &.previous-button svg,
  &.next-button svg {
    position: relative;
    width: 20px;
    height: 20px;
    font-size: 18px;
    min-height: 25px;
    &:before {
      position: absolute;
      top: 4px;
      left: -2px;
    }
    &:hover {
      cursor: pointer;
    }
  }
`;

const PlayerWrapper: any = styled.div`
  background-color: ${(props: any) =>
    props.customColors.length > 0 ? props.customColors[2] : "#000"};
`;
